import type { FunctionReturnType } from "convex/server";
import type { api } from "@/convex/_generated/api";
import { isOfflineCacheExpired } from "./offline-ttl";

/**
 * Snapshot local del "app shell": el contexto de usuario/empresa/sucursal/rol/
 * suscripción y el set completo de permisos. Es lo que permite que la app siga
 * NAVEGABLE cuando se cae la conexión con la sesión ya abierta (caso "warm"):
 * los hooks centrales (useCompanyContext, usePermissions, useCurrentUser) caen a
 * este snapshot cuando la query de Convex queda `undefined`.
 *
 * NO es fuente de verdad de seguridad: solo relaja el gating de UII. Todo write
 * pasa por mutations que el server revalida. Se limpia al cerrar sesión.
 *
 * Un solo slot por navegador (con el `userId` adentro): se sobrescribe al
 * sincronizar y se borra al sign-out, así no queda contexto de otro usuario.
 */

type CheckUserContextResult = FunctionReturnType<
  typeof api.system.context.queries.checkUserContext
>;

/** La rama "válida" del resultado de checkUserContext (no error, no null). */
export type AppShellContext = Extract<
  NonNullable<CheckUserContextResult>,
  { userId: unknown }
>;

export type AppShellPermissions = FunctionReturnType<
  typeof api.system.permissions.queries.getUserPermissions
>;

export interface AppShellSnapshot {
  userId: string;
  context: AppShellContext;
  permissions: AppShellPermissions;
  savedAt: number;
}

const KEY = "heskala:offline:appshell";

export function readAppShellSnapshot(): AppShellSnapshot | null {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as AppShellSnapshot;
    if (
      !parsed ||
      typeof parsed !== "object" ||
      !parsed.context ||
      !parsed.userId
    ) {
      return null;
    }
    // Vencido: no servir contexto/permisos viejos (seguridad) y limpiar.
    if (isOfflineCacheExpired(parsed.savedAt)) {
      clearAppShellSnapshot();
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

export function writeAppShellSnapshot(
  userId: string,
  data: { context: AppShellContext; permissions: AppShellPermissions },
): void {
  try {
    localStorage.setItem(
      KEY,
      JSON.stringify({
        userId,
        context: data.context,
        permissions: data.permissions,
        savedAt: Date.now(),
      } satisfies AppShellSnapshot),
    );
  } catch {
    // localStorage lleno/bloqueado: la app funciona online igual; solo se
    // pierde la resiliencia offline.
  }
}

export function clearAppShellSnapshot(): void {
  try {
    localStorage.removeItem(KEY);
  } catch {
    // no-op
  }
}
